import {
  matchPath,
  useLocation,
  useNavigate,
  type Location,
  type NavigateOptions,
  type To,
} from "react-router";

export type WizardLocationState = {
  backgroundLocation?: Location;
};

const CONFERENCE_WIZARD_PATH = "/conferences/new/*";

export function isConferenceWizardPath(pathname: string): boolean {
  return Boolean(
    matchPath({ path: CONFERENCE_WIZARD_PATH, end: false }, pathname),
  );
}

export function isWizardPath(pathname: string): boolean {
  return isConferenceWizardPath(pathname);
}

export function wizardUnderlayLocation(location: Location): Location {
  const state = location.state as WizardLocationState | null;
  if (state?.backgroundLocation) return state.backgroundLocation;

  if (isConferenceWizardPath(location.pathname)) {
    return {
      ...location,
      pathname: "/conferences",
      search: "",
      hash: "",
      state: null,
      key: "wizard-underlay",
    };
  }

  return location;
}

export function conferenceWizardPath(step = "introduction") {
  return `/conferences/new/${step}`;
}

/** Open a conference wizard over the current page (background preserved). */
export function useOpenWizard() {
  const navigate = useNavigate();
  const location = useLocation();

  return (step = "introduction") => {
    navigate(conferenceWizardPath(step), {
      state: { backgroundLocation: location } satisfies WizardLocationState,
    });
  };
}

/** In-wizard navigation that keeps the underlay page in location state. */
export function useWizardNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  const prior = (location.state as WizardLocationState | null) ?? null;

  return (to: To, options?: NavigateOptions) => {
    navigate(to, {
      ...options,
      state: {
        ...(typeof options?.state === "object" &&
        options.state &&
        !Array.isArray(options.state)
          ? options.state
          : {}),
        ...(prior?.backgroundLocation
          ? { backgroundLocation: prior.backgroundLocation }
          : {}),
      } satisfies WizardLocationState,
    });
  };
}

export function useWizardLinkState(): WizardLocationState | null {
  const location = useLocation();
  const state = location.state as WizardLocationState | null;
  if (!state?.backgroundLocation) return null;
  return { backgroundLocation: state.backgroundLocation };
}

export function useExitWizard() {
  const navigate = useNavigate();
  const location = useLocation();

  return (fallbackPath?: string) => {
    const background = (location.state as WizardLocationState | null)
      ?.backgroundLocation;
    if (background) {
      navigate(background);
      return;
    }
    if (fallbackPath) {
      navigate(fallbackPath);
      return;
    }
    navigate("/conferences");
  };
}
